import { Close16Icon } from '@jablotron/lib.spa-icons/icons/close-16';
import { Search16Icon } from '@jablotron/lib.spa-icons/icons/search-16';
import { useTranslate } from '@jablotron/lib.spa-lang';
import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { LanguageSwitcher } from '~/components/language/LanguageSwitcher';
import { Texts } from '~/texts/runtime';
import { CatalogSearchBox } from '~/widgets/catalog-search';
import { CatalogMobileMenu } from '~/widgets/catalog-sidebar';
import { MegaMenu } from '~/widgets/mega-menu';

export function Header() {
  const t = useTranslate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isHomepage = pathname === '/';
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => {
    setMobileSearchOpen(false);
  }, [pathname]);

  // The mobile search bar only ever takes over the header below sm. The standard header stays
  // rendered and is merely hidden there, so resizing past the breakpoint with the search open
  // can never leave the bar empty.
  const mobileSearchActive = mobileSearchOpen && !isHomepage;
  const barClass = mobileSearchActive ? 'hidden sm:flex' : 'flex';

  return (
    <header className="sticky top-0 z-50 border-b border-primary bg-white">
      <nav className="relative mx-auto flex h-14 max-w-[1440px] items-center gap-3 px-3 sm:h-16 sm:gap-12 sm:px-6 lg:px-8">
        {mobileSearchActive && (
          <div className="flex w-full items-center gap-1 sm:hidden">
            <CatalogSearchBox className="min-w-0 flex-1" autoFocus />
            <button
              type="button"
              onClick={() => setMobileSearchOpen(false)}
              className="flex shrink-0 items-center justify-center rounded-lg border-none bg-transparent p-2 text-brand-muted hover:text-brand-ink"
              aria-label={t(Texts.SearchClose)}
              title={t(Texts.SearchClose)}
            >
              <Close16Icon width={16} height={16} aria-hidden />
            </button>
          </div>
        )}

        <div className={`${barClass} shrink-0 items-center gap-2 sm:gap-4`}>
          <CatalogMobileMenu />

          <Link to="/" className="absolute left-1/2 shrink-0 -translate-x-1/2 no-underline sm:static sm:translate-x-0">
            <img
              src="/images/logo-header.png"
              alt={t(Texts.BrandName)}
              width={756}
              height={111}
              className="block h-6 w-auto sm:h-7"
            />
          </Link>
        </div>

        <div className={`${barClass} min-w-0 flex-1 items-center gap-8 sm:gap-12`}>
          {!isHomepage && <CatalogSearchBox className="hidden min-w-0 max-w-[320px] sm:block" />}

          <div className="hidden items-center gap-4 lg:flex">
            <MegaMenu />

            <Link
              to="/compatibility"
              className="whitespace-nowrap text-sm font-normal text-brand-muted no-underline transition-colors hover:text-brand-ink"
            >
              {t(Texts.FooterProductCompatibility)}
            </Link>

            <Link
              to="/blog"
              className="whitespace-nowrap text-sm font-normal text-brand-muted no-underline transition-colors hover:text-brand-ink"
            >
              {t(Texts.NavBlog)}
            </Link>
          </div>
        </div>

        <div className={`${barClass} shrink-0 items-center gap-1 sm:gap-4`}>
          {!isHomepage && (
            <button
              type="button"
              onClick={() => setMobileSearchOpen(true)}
              className="flex items-center justify-center rounded-lg border-none bg-transparent p-2 text-brand-ink sm:hidden"
              aria-label={t(Texts.SearchSubmit)}
              title={t(Texts.SearchSubmit)}
            >
              <Search16Icon width={18} height={18} aria-hidden />
            </button>
          )}

          <LanguageSwitcher />
        </div>
      </nav>
    </header>
  );
}
